import React from "react";
import { ColumnDef, flexRender, Table } from "@tanstack/react-table";
import { Pagination, Spinner, Text } from "@telia/teddy";

interface NoOrdersBodyProps {
    error: Error | null;
    isLoading: boolean;
}

const NoOrdersBody = ({ error, isLoading }: NoOrdersBodyProps) => {
    if (isLoading) {
        return <Spinner size="lg" />;
    }
    if (error) {
        return <Text>{error.message}</Text>;
    }
    return <Text variant="additional-100">Ingen ordre funnet</Text>;
};

interface OrdersTableProps<T> {
    columns: ColumnDef<T>[];
    error: Error | null;
    getCellHref?: (row: T, columnId: string) => string | undefined;
    isLoading: boolean;
    isRowExpanded?: (row: T) => boolean;
    onRowClick: (row: T) => void;
    renderExpandedRow?: (row: T) => React.ReactNode;
    table: Table<T>;
}

const OrdersTable = <T,>({
    columns,
    error,
    getCellHref,
    isLoading,
    isRowExpanded,
    onRowClick,
    renderExpandedRow,
    table,
}: OrdersTableProps<T>) => {
    const columnsCount = columns.length;
    return (
        <>
            <div className="orders__table-wrapper">
                <table className="orders__table">
                    <colgroup>
                        {columns.map((col, index) => (
                            <col key={index} className={`orders__col--${col.id}`} />
                        ))}
                    </colgroup>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id} className="orders__th">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.length === 0 ? (
                            <tr>
                                <td colSpan={columnsCount} className="orders__td-loader">
                                    <NoOrdersBody error={error} isLoading={isLoading} />
                                </td>
                            </tr>
                        ) : (
                            table.getRowModel().rows.map((row) => (
                                <React.Fragment key={row.id}>
                                    <tr
                                        className={`orders__tr${isRowExpanded?.(row.original) ? " orders__tr--expanded" : ""}${getCellHref ? " orders__tr--cell-links" : ""}`}
                                        onClick={getCellHref ? undefined : () => onRowClick(row.original)}
                                    >
                                        {row.getVisibleCells().map((cell) => {
                                            const cellHref = getCellHref?.(row.original, cell.column.id);
                                            return (
                                                <td
                                                    key={cell.id}
                                                    className={`orders__td${cellHref ? " orders__td--link" : ""}`}
                                                >
                                                    {cellHref ? (
                                                        <a
                                                            href={cellHref}
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                onRowClick(row.original);
                                                            }}
                                                        >
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </a>
                                                    ) : (
                                                        flexRender(cell.column.columnDef.cell, cell.getContext())
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                    {isRowExpanded?.(row.original) && renderExpandedRow && (
                                        <tr className="orders__tr-expand">
                                            <td colSpan={columnsCount} className="orders__td-expand">
                                                {renderExpandedRow(row.original)}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {!isLoading && !error && table.getPageCount() > 1 && (
                <Pagination aria-label="Sidenavigasjon" className="orders__pagination">
                    <Pagination.Navigation
                        currentPage={table.getState().pagination.pageIndex + 1}
                        totalPages={table.getPageCount()}
                        onPageChange={(page) => table.setPageIndex(page - 1)}
                        side="prev"
                        showLabels={false}
                    />
                    <Pagination.PageNumbers
                        currentPage={table.getState().pagination.pageIndex + 1}
                        totalPages={table.getPageCount()}
                        onPageChange={(page) => table.setPageIndex(page - 1)}
                        maxVisiblePages={5}
                    />
                    <Pagination.Navigation
                        currentPage={table.getState().pagination.pageIndex + 1}
                        totalPages={table.getPageCount()}
                        onPageChange={(page) => table.setPageIndex(page - 1)}
                        side="next"
                        showLabels={false}
                    />
                </Pagination>
            )}
        </>
    );
};

export default OrdersTable;
